import { useState } from "react";
import { motion } from "framer-motion";
import { Gift, CheckCircle2, ArrowRight, Sparkles, Calendar } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const BOOKING_URL = "https://agendamiento.reservo.cl/makereserva/agenda/v0XFJkK0p04Joq5h8h11dw79n3v0yK";

const benefits = [
  "Sesión de 45 minutos con entrenador personal",
  "Evaluación de tu condición física",
  "Plan de acción personalizado para tus metas",
  "Sin compromiso, sin letra chica",
];

export const FreeClassSection = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `¡Hola! Soy ${name} y quiero agendar mi clase de prueba en Inspirion GYM. Mi número es ${phone}`;
    const whatsappUrl = `https://wa.me/56966951278?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const directWhatsappUrl = `https://wa.me/56966951278?text=${encodeURIComponent("¡Hola! Quiero agendar mi clase de prueba en Inspirion GYM")}`;

  return (
    <section 
      id="clase-prueba"
      data-testid="free-class-section"
      className="relative py-24 bg-dark-paper overflow-hidden"
    >
      {/* Neon glow background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, rgba(0, 255, 0, 0.3) 0%, transparent 70%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex-1"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neon-green/10 border border-neon-green/30 text-neon-green text-sm font-bold uppercase tracking-wider mb-6">
              <Gift className="w-4 h-4" />
              Oferta Especial
            </div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-tight">
              Agenda tu
              <br />
              <span className="text-neon-green text-glow-green">Clase de Prueba</span>
            </h2>
            
            <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-xl">
              Ven a conocernos sin compromiso. Experimenta de primera mano lo que 
              es entrenar con acompañamiento real y un plan diseñado para ti.
            </p>

            {/* Benefits */}
            <ul className="mt-8 space-y-4">
              {benefits.map((benefit, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-neon-green flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{benefit}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Form Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-1 w-full max-w-md"
          >
            <div 
              className="relative p-8 bg-dark-card border border-neon-green/20"
              style={{ boxShadow: "0 0 40px rgba(0, 255, 0, 0.1)" }}
            >
              {/* Sparkle decoration */}
              <div className="absolute -top-3 -right-3">
                <Sparkles className="w-6 h-6 text-neon-green animate-pulse" />
              </div>

              <h3 className="text-2xl font-bold text-white uppercase tracking-tight mb-2">
                Agenda tu clase
              </h3>
              <p className="text-gray-400 mb-6">
                Déjanos tus datos y te contactamos por WhatsApp
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Tu nombre"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="free-class-name-input"
                    className="bg-black border-white/10 text-white placeholder:text-gray-500 focus:border-neon-green h-12"
                    required
                  />
                </div>
                <div>
                  <Input
                    type="tel"
                    placeholder="Tu WhatsApp (ej: +56 9 1234 5678)"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="free-class-phone-input"
                    className="bg-black border-white/10 text-white placeholder:text-gray-500 focus:border-neon-green h-12"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="free-class-submit"
                  className="w-full group bg-neon-green hover:bg-neon-green/90 text-black font-bold text-lg py-6 rounded-none uppercase tracking-wide"
                  style={{ boxShadow: "0 0 30px rgba(0, 255, 0, 0.4)" }}
                >
                  Agendar mi clase de prueba
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>

              {/* Or divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-gray-500 text-sm">o</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Direct WhatsApp */}
              <Button
                asChild
                variant="outline"
                data-testid="free-class-whatsapp-direct"
                className="w-full border-2 border-neon-whatsapp bg-transparent text-neon-whatsapp hover:bg-neon-whatsapp/10 font-bold py-6 rounded-none uppercase tracking-wide"
              >
                <a href={directWhatsappUrl} target="_blank" rel="noopener noreferrer">
                  Escribir por WhatsApp directo
                </a>
              </Button>

              {/* Online Booking */}
              <Button
                asChild
                variant="outline"
                data-testid="free-class-booking-online"
                className="w-full border-2 border-neon-cyan bg-transparent text-neon-cyan hover:bg-neon-cyan/10 font-bold py-6 rounded-none uppercase tracking-wide"
              >
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Calendar className="w-5 h-5 mr-2" />
                  Agendar Online
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FreeClassSection;
